import React, { useState } from 'react';
import { Language } from '@/types';
import { Modal, Button, Textarea, Alert } from '@/components/common';
import { getTranslation } from '@/constants';
import { createRating } from '@/services';
import { useAuth } from '@/context/AuthContext';

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  exchangeId: string | null;
  toUserId: string | null;
  toUserName: string | null;
  language: Language;
}

export const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  exchangeId,
  toUserId,
  toUserName,
  language,
}) => {
  const { user } = useAuth();
  const t = getTranslation(language);

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!exchangeId || !toUserId || !user || rating === 0) {
      setError('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createRating(exchangeId, user.uid, toUserId, rating, comment);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setRating(0);
        setComment('');
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError('Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
      setRating(0);
      setComment('');
      setError(null);
      setSuccess(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t.rateExchange}
      size="md"
    >
      {success ? (
        <Alert variant="success">{t.success}</Alert>
      ) : (
        <div className="space-y-4">
          {error && (
            <Alert variant="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'en'
                ? `How was your experience with ${toUserName}?`
                : `Comment s'est passée votre expérience avec ${toUserName}?`}
            </p>

            <div className="flex justify-center space-x-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="text-4xl transition-transform hover:scale-110 focus:outline-none"
                >
                  <span
                    className={
                      star <= (hoveredRating || rating)
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    }
                  >
                    ★
                  </span>
                </button>
              ))}
            </div>

            <p className="text-center text-sm text-gray-500">
              {rating > 0 &&
                (language === 'en'
                  ? `${rating} star${rating > 1 ? 's' : ''}`
                  : `${rating} étoile${rating > 1 ? 's' : ''}`)}
            </p>
          </div>

          <Textarea
            label={t.comment}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            maxLength={300}
            helperText={`${comment.length}/300`}
          />

          <div className="flex space-x-3">
            <Button
              variant="secondary"
              fullWidth
              onClick={handleClose}
              disabled={submitting}
            >
              {t.cancel}
            </Button>
            <Button
              fullWidth
              onClick={handleSubmit}
              loading={submitting}
              disabled={rating === 0}
            >
              {t.submitRating}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
